import Link from 'next/link';
import ChevronIcon from '@/components/icons/chevron';
import type { CenterDoctorLink } from '@/modules/profile/functions/getCenterDoctorLinks';

const toPersianDigits = (value: number) => String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

/**
 * The centre's doctors as real <a href="/dr/<slug>/"> links, collapsed behind one row.
 * <details> keeps every anchor in the server HTML while closed (no JS needed to open it).
 * The interactive list above only renders 20 doctors, and only after JS.
 * `doctors` comes from getCenterDoctorLinks (same search query and visibility filters as that list).
 */
export const CenterDoctorLinks = ({ centerName, doctors }: { centerName?: string; doctors?: CenterDoctorLink[] }) => {
  const items = (doctors ?? []).filter(doctor => typeof doctor?.url === 'string' && doctor.url.startsWith('/dr/'));
  if (!items.length) return null;
  const summary = `همه پزشکان این مرکز (${toPersianDigits(items.length)})`;

  return (
    <details className="group bg-white md:rounded-lg">
      <summary className="flex items-center justify-between p-4 text-sm font-bold list-none cursor-pointer select-none [&::-webkit-details-marker]:hidden">
        <span>{summary}</span>
        <span className="transition-transform group-open:rotate-180">
          <ChevronIcon dir="bottom" />
        </span>
      </summary>
      <nav aria-label={`پزشکان ${centerName || 'این مرکز'}`} className="px-4 pb-4">
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          {items.map(doctor => (
            <li key={doctor.url} className="border-b border-slate-100">
              <Link
                href={doctor.url}
                prefetch={false}
                title={doctor.expertise || undefined}
                className="flex items-center min-h-[44px] py-2 text-sm text-slate-700 hover:text-primary"
              >
                {doctor.name}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </details>
  );
};

export default CenterDoctorLinks;
